// src/components/sections/PatientJourney.js
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
    FaStethoscope, FaFileMedical, FaProcedures,
    FaHeartbeat, FaUserFriends, FaCalendarCheck
} from 'react-icons/fa';

const PatientJourney = () => {
    const journeySteps = [
        {
            step: "01",
            icon: <FaStethoscope />,
            title: "Initial Consultation",
            description: "Comprehensive evaluation and diagnosis",
            details: "Meet with specialists, discuss concerns, and undergo initial assessments",
            color: "#20B2AA"
        },
        {
            step: "02",
            icon: <FaFileMedical />,
            title: "Personalized Care Plan",
            description: "Tailored treatment strategy",
            details: "Customized plan developed by multi-disciplinary team",
            color: "#87CEEB"
        },
        {
            step: "03",
            icon: <FaProcedures />,
            title: "Treatment & Procedure",
            description: "Expert medical intervention",
            details: "Advanced treatment with modern technology and expert care",
            color: "#FF7F50"
        },
        {
            step: "04",
            icon: <FaHeartbeat />,
            title: "Recovery & Monitoring",
            description: "Comprehensive post-care",
            details: "Close monitoring and support during recovery phase",
            color: "#9370DB"
        },
        {
            step: "05",
            icon: <FaUserFriends />,
            title: "Follow-up Care",
            description: "Ongoing support system",
            details: "Regular check-ups and continued care coordination",
            color: "#3CB371"
        },
        {
            step: "06",
            icon: <FaCalendarCheck />,
            title: "Wellness & Prevention",
            description: "Long-term health maintenance",
            details: "Preventive care and health education for sustained wellbeing",
            color: "#FFD700"
        }
    ];

    return (
        <section id="patient-journey" className="section-padding">
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                        Your Care Journey With Us
                    </h2>
                    <p className="lead" style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>
                        A seamless, compassionate healthcare experience from consultation to recovery
                    </p>
                </motion.div>

                <Row className="g-4">
                    {journeySteps.map((step, index) => (
                        <Col lg={4} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div className="position-relative h-100">
                                    {/* Step connector line */}
                                    {index < journeySteps.length - 1 && (
                                        <div
                                            className="position-absolute d-none d-lg-block"
                                            style={{
                                                top: '50%',
                                                right: '-20px',
                                                width: '40px',
                                                height: '2px',
                                                backgroundColor: '#e0e0e0',
                                                zIndex: 1
                                            }}
                                        ></div>
                                    )}

                                    <div
                                        className="healthcare-card border-0 h-100 p-4 position-relative"
                                        style={{ zIndex: 2 }}
                                    >
                                        <div className="d-flex align-items-start mb-4">
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center me-4"
                                                style={{
                                                    width: '70px',
                                                    height: '70px',
                                                    backgroundColor: `${step.color}20`,
                                                    color: step.color,
                                                    fontSize: '1.8rem',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {step.icon}
                                            </div>
                                            <div>
                                                <span
                                                    className="badge mb-2"
                                                    style={{
                                                        backgroundColor: step.color,
                                                        fontSize: '0.8rem',
                                                        padding: '4px 12px'
                                                    }}
                                                >
                                                    Step {step.step}
                                                </span>
                                                <h5 className="fw-bold mb-2" style={{ color: 'var(--dark-teal)' }}>
                                                    {step.title}
                                                </h5>
                                                <p className="mb-1" style={{ color: step.color, fontWeight: '500' }}>
                                                    {step.description}
                                                </p>
                                            </div>
                                        </div>

                                        <p style={{ color: '#666', fontSize: '0.95rem' }}>
                                            {step.details}
                                        </p>

                                        <div className="mt-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="rounded-circle me-2"
                                                    style={{
                                                        width: '8px',
                                                        height: '8px',
                                                        backgroundColor: step.color
                                                    }}
                                                ></div>
                                                <small style={{ color: '#888' }}>
                                                    Average duration: {index < 2 ? '1-2 hours' : index < 4 ? 'Varies' : '30 mins'}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="mt-5 pt-4"
                >
                    <div
                        className="p-5 rounded text-center"
                        style={{
                            background: 'linear-gradient(135deg, var(--teal-light) 0%, white 100%)',
                            border: '2px dashed var(--primary-teal)'
                        }}
                    >
                        <h4 className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                            Personalized Support Every Step of the Way
                        </h4>
                        <p className="mb-0" style={{ maxWidth: '800px', margin: '0 auto', color: '#666' }}>
                            Our dedicated care coordinators guide you through every stage, ensuring
                            seamless communication, appointment scheduling, and answering all your questions.
                        </p>
                    </div>
                </motion.div>
            </Container>
        </section>
    );
};

export default PatientJourney;